import { Suspense, useEffect } from "react";
import { createFileRoute, Outlet, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ClerkLoading, RedirectToSignIn, Show, useAuth } from "@clerk/react";
import { TriangleAlert } from "lucide-react";
import { MainLayout } from "@/components/common/layout/main-layout";
import { CopilotPanel } from "@/components/copilot/copilot-panel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { getErrorMessage } from "@/lib/handle-mutation-error";
import { QueryProvider } from "@/providers/query-provider";
import { SalonIntlProvider } from "@/providers/salon-intl-provider";

// The signed-in app. The salon is the Clerk session's active organization (never the URL); the backend reads it from
// the verified token.
export const Route = createFileRoute("/_app")({
  component: SignedInApp,
  errorComponent: SignedInAppError,
});

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner className="size-6" />
    </div>
  );
}

function SignedInApp() {
  const { orgId } = useAuth();
  return (
    <>
      <ClerkLoading>
        <FullPageSpinner />
      </ClerkLoading>
      <Show when="signed-out">
        <RedirectToSignIn />
      </Show>
      <Show when="signed-in">
        {/* Keyed by salon: a switch (in this tab or another) starts a fresh query cache and page, so nothing loaded
            for one salon is shown, or saved by id, in another. */}
        <QueryProvider key={orgId}>
          <Suspense fallback={<FullPageSpinner />}>
            <SalonIntlProvider>
              <MainLayout>
                <Outlet />
                <CopilotPanel />
              </MainLayout>
            </SalonIntlProvider>
          </Suspense>
        </QueryProvider>
      </Show>
    </>
  );
}

// TanStack's pattern for suspense queries: reset the failed queries, then re-run the route.
function SignedInAppError({ error }: ErrorComponentProps) {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();
  useEffect(() => queryErrorResetBoundary.reset(), [queryErrorResetBoundary]);
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-l-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TriangleAlert className="h-6 w-6" />
            Unable to load Zarlini
          </CardTitle>
        </CardHeader>
        <CardContent className="ml-8 flex flex-col items-start gap-4">
          <p>{getErrorMessage(error)}</p>
          <Button onClick={() => router.invalidate()}>Try again</Button>
        </CardContent>
      </Card>
    </div>
  );
}
